import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useRegister } from '@/features/auth/useAuth'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter'
import type { ApiError } from '@/api/client'

export default function Register() {
  const register = useRegister()
  const [form, setForm] = useState({
    agencyName: '',
    slug: '',
    fullName: '',
    email: '',
    password: '',
  })

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    register.mutate(form)
  }

  const error = register.error as ApiError | null

  // Verification enabled: the sign-up succeeded but no session was created — prompt for the inbox.
  if (register.data?.verificationRequired) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm text-center">
          <img src="/brand/profitpipe-wordmark.svg" alt="ProfitPipe" className="h-5 mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-brand-darkest mb-4">Check your email</h1>
          <p className="text-sm text-gray-500 mb-6">
            We've sent a confirmation link to <span className="font-medium text-brand-darkest">{form.email}</span>.
            Follow it to activate your workspace, then sign in.
          </p>
          <Link
            to="/login"
            className="inline-block w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    )
  }

  const field = (
    key: keyof typeof form,
    label: string,
    type = 'text',
    placeholder = '',
  ) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => set(key, e.target.value)}
        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
      />
    </div>
  )

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm">
        <img src="/brand/profitpipe-wordmark.svg" alt="ProfitPipe" className="h-5 mx-auto mb-5" />
        <h1 className="text-2xl font-bold text-brand-darkest text-center mb-6">Create your workspace</h1>

        <form onSubmit={onSubmit}>
          {field('agencyName', 'Agency name', 'text', 'Calibre Real Estate')}
          {field('slug', 'Workspace slug', 'text', 'calibre')}
          {field('fullName', 'Your name', 'text', 'Jane Agent')}
          {field('email', 'Email', 'email')}
          {field('password', 'Password (min 10, letters & numbers)', 'password')}
          <PasswordStrengthMeter password={form.password} />

          {error && (
            <p className="text-danger text-xs mb-3">
              {error.message}
              {error.details?.map((d) => (
                <span key={d.field} className="block">
                  {d.field}: {d.message}
                </span>
              ))}
            </p>
          )}

          <button
            type="submit"
            disabled={register.isPending}
            className="w-full bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-60 mt-2"
          >
            {register.isPending ? 'Creating…' : 'Create workspace'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          Already have one?{' '}
          <Link to="/login" className="text-brand-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
